/**
 * Dit si le navigateur ouvre le carnet d'adresses du téléphone.
 *
 * <p>L'API Contacts est ouverte d'office dans Chrome sur Android. Safari sur
 * iPhone l'a aussi, mais derrière un réglage (« Contact Picker API ») ; une
 * fois allumé, ce même test le voit. Ailleurs, voir `environnementContacts`.
 *
 * @returns {boolean} vrai si `navigator.contacts.select` est disponible
 */
export function contactsDispo() {
  if (estAppliNative()) return true;
  try {
    return (
      typeof navigator !== 'undefined' &&
      navigator.contacts &&
      typeof navigator.contacts.select === 'function'
    );
  } catch (e) {
    return false;
  }
}

/** Navigateurs de l'iPhone autres que Safari, et vues web des applications. */
const AUTRE_NAVIGATEUR_IOS =
  /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|YaBrowser|DuckDuckGo|GSA\/|FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Snapchat|Line\//;

/** Vue web d'une application Android, ou navigateur sans carnet d'adresses. */
const VUE_WEB_ANDROID = /; wv\)|FBAN|FBAV|FB_IAB|Instagram/;

/**
 * Le téléphone, ou l'ordinateur, sur lequel « Depuis mes contacts » est touché.
 *
 * <p>Chaque cas a sa réponse : le carnet s'ouvre (application, Chrome sur
 * Android, Safari réglé) ; Safari sur iPhone attend son réglage ; un autre
 * navigateur de l'iPhone ne l'aura jamais et doit passer par Safari ; sur
 * Android, Firefox, Samsung Internet ou la vue web d'une application doivent
 * passer par Chrome ; un ordinateur n'a pas de répertoire de téléphone.
 *
 * @returns {'natif'|'carnet'|'ios-safari'|'ios-autre'|'android-autre'|'ordinateur'}
 *   l'environnement reconnu
 */
export function environnementContacts() {
  if (estAppliNative()) return 'natif';
  if (contactsDispo()) return 'carnet';
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const ua = nav.userAgent || '';
  const ios =
    /iPhone|iPad|iPod/.test(ua) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
  if (ios) return AUTRE_NAVIGATEUR_IOS.test(ua) ? 'ios-autre' : 'ios-safari';
  if (/Android/i.test(ua) || (nav.userAgentData && nav.userAgentData.mobile)) {
    return 'android-autre';
  }
  return 'ordinateur';
}

/** Vrai dans la vue web d'une application Android (WhatsApp, Facebook…). */
export function estVueWebAndroid() {
  return typeof navigator !== 'undefined' && VUE_WEB_ANDROID.test(navigator.userAgent || '');
}

/** Champs que tous les carnets connaissent : Safari n'annonce pas l'adresse. */
const CHAMPS_SURS = ['name', 'email', 'tel'];

/** Champs du carnet, appris à l'affichage du bouton (voir `prepareCarnet`). */
let champsDuCarnet = null;

/**
 * Demande au navigateur, dès l'affichage du bouton, les champs que son carnet
 * sait rendre.
 *
 * <p>Le carnet ne s'ouvre que dans le geste même du doigt : Safari refuse
 * `select()` venu après une attente. La question se pose donc à l'avance, et
 * le toucher ouvre le carnet sans rien attendre.
 */
export function prepareCarnet() {
  if (champsDuCarnet || estAppliNative()) return;
  try {
    const gestionnaire = typeof navigator !== 'undefined' ? navigator.contacts : null;
    if (!gestionnaire || typeof gestionnaire.getProperties !== 'function') return;
    Promise.resolve(gestionnaire.getProperties())
      .then((sup) => {
        const voulus = ['name', 'tel', 'email', 'address'].filter((p) => (sup || []).includes(p));
        champsDuCarnet = voulus.length ? voulus : ['name'];
      })
      .catch(() => {});
  } catch (e) {
    // Rien d'annoncé : les champs sûrs serviront.
  }
}

/** Oublie les champs appris : le carnet a refusé la demande. */
export function oublieChampsDuCarnet() {
  champsDuCarnet = CHAMPS_SURS;
}

/**
 * SwissTech tourne-t-il dans l'application installée depuis l'App Store ou
 * Google Play ? Elle ouvre alors le vrai carnet d'adresses du téléphone, sans
 * réglage, quel que soit le téléphone.
 *
 * @returns {boolean} vrai dans l'application native
 */
export function estAppliNative() {
  try {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null;
    return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
  } catch (e) {
    return false;
  }
}

/**
 * Rue d'un côté, NPA et localité de l'autre, depuis une adresse de contact.
 *
 * <p>Les carnets rendent l'adresse de façons diverses : la rue sur deux
 * lignes, la ligne « 1287 Laconnex » mêlée à la rue, la ville répétée, la rue
 * en double. Repris tels quels, l'offre montrait « Rte de la Parraille 7, »
 * puis « Rte de la Parraille 7, 1287 Laconnex ». Chaque ligne de rue perd ici
 * son NPA et sa localité, les doublons partent, et le NPA trouvé dans une
 * ligne sert quand le carnet ne le donne pas à part.
 *
 * @param {string|string[]} rue ligne(s) de rue, sauts de ligne compris
 * @param {string} [npa] code postal donné à part
 * @param {string} [ville] localité donnée à part
 * @returns {{adr: string, npa: string}} la rue et « NPA localité »
 */
export function rueEtLocalite(rue, npa, ville) {
  let cp = String(npa || '').trim();
  let lieu = String(ville || '').trim();
  const lignes = (Array.isArray(rue) ? rue : [rue])
    .flatMap((l) => String(l || '').split(/\n/))
    .map((l) => l.trim())
    .filter(Boolean);
  if (!cp) {
    for (const l of lignes) {
      const m = l.match(/(?:^|[,\s])(\d{4,5})\s+([^,\d][^,]*)$/);
      if (m) {
        cp = m[1];
        lieu = lieu || m[2].trim();
        break;
      }
    }
  }
  const echappe = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const rues = [];
  for (const l of lignes) {
    let r = l;
    if (cp) r = r.replace(new RegExp('(^|[,\\s])' + echappe(cp) + '\\b.*$'), '');
    if (lieu) r = r.replace(new RegExp('\\s*,\\s*' + echappe(lieu) + '$', 'i'), '');
    r = r.replace(/[,\s]+$/, '').trim();
    if (
      r &&
      r.toLowerCase() !== lieu.toLowerCase() &&
      !rues.some((x) => x.toLowerCase() === r.toLowerCase())
    ) {
      rues.push(r);
    }
  }
  return { adr: rues.join(', '), npa: [cp, lieu].filter(Boolean).join(' ') };
}

/**
 * Les champs d'un client tirés d'un contact du carnet natif (iPhone ou
 * Android) : nom, rue, NPA et localité, téléphone, e-mail — tout ce que le
 * patron a enregistré sur la fiche.
 *
 * @param {object} contact contact rendu par le plugin natif
 * @returns {{nom: string, adr: string, npa: string, tel: string, email: string}} les champs
 */
export function contactDuNatif(contact) {
  const c = contact || {};
  const n = c.name || {};
  const nom =
    (n.display || [n.given, n.family].filter(Boolean).join(' ')).trim() ||
    (c.organization?.company || '').trim();
  const principal = (liste) =>
    (liste || []).find((x) => x && x.isPrimary) || (liste || []).find(Boolean) || null;
  const adresse = principal(c.postalAddresses);
  const tel = principal(c.phones);
  const email = principal(c.emails);
  return {
    nom,
    ...(adresse
      ? rueEtLocalite(adresse.street, adresse.postcode, adresse.city)
      : { adr: '', npa: '' }),
    tel: (tel?.number || '').trim(),
    email: (email?.address || '').trim(),
  };
}

/**
 * Lit une fiche vCard et en tire les champs d'un client.
 *
 * <p>Cette lecture existait en double, à l'accueil et dans la page des clients,
 * caractère pour caractère. Une vCard mal formée corrigée d'un côté serait
 * restée illisible de l'autre.
 *
 * <p>Le format vCard laisse le nom s'écrire de deux façons : {@code FN}, déjà
 * mis en forme, et {@code N}, découpé en nom de famille puis prénom. Le premier
 * est préféré ; le second lui sert de repli, recomposé dans l'ordre de lecture.
 *
 * @param {string} texte contenu du fichier
 * @returns {{nom: string, adr: string, npa: string, tel: string, email: string}}
 *   les champs trouvés, chacun vide à défaut
 */
export function contactDepuisVCard(texte) {
  // Une ligne trop longue se replie sur la suivante, qui commence alors par
  // une espace : on la recolle avant toute lecture. La première fiche seule
  // compte, si le fichier en porte plusieurs.
  const brut = String(texte || '')
    .replace(/\r/g, '')
    .replace(/\n[ \t]/g, '');
  const fin = brut.search(/\nEND:VCARD/i);
  const t = '\n' + (fin >= 0 ? brut.slice(0, fin) : brut);
  // Valeur du premier champ de ce nom, avec ou sans groupe (« item1.EMAIL »)
  // et paramètres (« ;type=CELL »), décodée et déséchappée.
  const champ = (nom) => {
    const m = t.match(new RegExp('\\n(?:[\\w-]+\\.)?' + nom + '((?:;[^:\\n]*)?):([^\\n]*)', 'i'));
    if (!m) return '';
    const qp = /ENCODING=QUOTED-PRINTABLE/i.test(m[1]);
    return qp ? decodeQuotedPrintable(m[2], m[1]) : m[2];
  };
  const net = (v) => deseschappe(v).replace(/\s+/g, ' ').trim();

  let nom = net(champ('FN'));
  if (!nom) {
    const n = champ('N');
    if (n) {
      const p = n.split(/(?<!\\);/).map(net);
      nom = [p[1], p[0]].filter(Boolean).join(' ').trim() || p[0] || '';
    }
  }
  // Une fiche d'entreprise sans nom de personne porte celui de la société.
  if (!nom) nom = net(champ('ORG').split(/(?<!\\);/)[0] || '');

  let adr = '';
  let npa = '';
  const a = champ('ADR');
  if (a) {
    // Les champs d'une adresse vCard : boîte ; complément ; rue ; localité ;
    // région ; code postal ; pays. La rue peut tenir sur deux lignes.
    const p = a.split(/(?<!\\);/).map((x) => deseschappe(x).trim());
    ({ adr, npa } = rueEtLocalite(p[2], p[5], p[3]));
  }

  return {
    nom,
    adr,
    npa,
    tel: net(champ('TEL')).replace(/^tel:/i, ''),
    email: net(champ('EMAIL')),
  };
}

/** Rend le texte d'une valeur vCard : « \, », « \; », « \n » et « \\ ». */
function deseschappe(valeur) {
  return String(valeur || '').replace(/\\([nN,;\\])/g, (_, c) =>
    c === 'n' || c === 'N' ? '\n' : c,
  );
}

/** Décode une valeur « quoted-printable » des anciennes fiches (vCard 2.1, Android). */
function decodeQuotedPrintable(valeur, parametres) {
  const octets = [];
  const v = String(valeur || '').replace(/=$/, '');
  for (let i = 0; i < v.length; i += 1) {
    if (v[i] === '=' && /^[0-9A-F]{2}$/i.test(v.slice(i + 1, i + 3))) {
      octets.push(parseInt(v.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      octets.push(v.charCodeAt(i));
    }
  }
  const latin = /CHARSET=(ISO-8859-1|WINDOWS-1252)/i.test(parametres || '');
  try {
    return new TextDecoder(latin ? 'windows-1252' : 'utf-8').decode(new Uint8Array(octets));
  } catch (e) {
    return v;
  }
}

/**
 * Le client déjà enregistré qui correspond à un contact, s'il y en a un.
 *
 * <p>Choisir deux fois la même personne dans les contacts créait deux clients.
 * Même adresse e-mail, même numéro de téléphone, ou même nom à la même
 * localité : c'est le même client.
 *
 * @param {object[]} clients clients de l'entreprise
 * @param {{nom: string, npa: string, tel: string, email: string}} contact contact choisi
 * @returns {object|undefined} le client existant
 */
export function clientExistant(clients, contact) {
  const c = contact || {};
  const chiffres = (v) =>
    String(v || '')
      .replace(/\D/g, '')
      .replace(/^(00)?41/, '0');
  const bas = (v) =>
    String(v || '')
      .trim()
      .toLowerCase();
  return (clients || []).find(
    (x) =>
      (bas(c.email) && bas(x.email) === bas(c.email)) ||
      (chiffres(c.tel).length >= 9 && chiffres(x.tel) === chiffres(c.tel)) ||
      (bas(c.nom) && bas(x.nom) === bas(c.nom) && bas(x.npa) === bas(c.npa)),
  );
}

/**
 * Tire les champs d'un client d'un contact rendu par le carnet d'adresses.
 *
 * <p>Même lecture que ci-dessus, mais depuis l'objet que l'API Contacts du
 * navigateur fournit : l'accueil, la page des clients et la décharge en
 * portaient chacun leur exemplaire.
 *
 * @param {object} contact contact tel que {@code navigator.contacts} le rend
 * @returns {{nom: string, adr: string, npa: string, tel: string, email: string}}
 *   les champs trouvés, chacun vide à défaut
 */
export function contactDuCarnet(contact) {
  const c = contact || {};
  let adr = '';
  let npa = '';
  if (c.address && c.address[0]) {
    const a = c.address[0];
    ({ adr, npa } = rueEtLocalite(a.addressLine || [], a.postalCode, a.city));
  }
  return {
    nom: (c.name && c.name[0]) || '',
    adr,
    npa,
    tel: (c.tel && c.tel[0]) || '',
    email: (c.email && c.email[0]) || '',
  };
}

/**
 * Ouvre le carnet d'adresses du téléphone et rend le contact choisi.
 *
 * <p>Tous les navigateurs n'exposent pas les mêmes champs : on ne demande que
 * ceux qu'il déclare connaître, faute de quoi l'appel échoue en bloc et
 * l'utilisateur n'obtient rien.
 *
 * @returns {Promise<object|null>} les champs du contact, ou {@code null} si
 *   aucun n'a été choisi
 * @throws {Error} si le carnet refuse de s'ouvrir
 */
export async function choisitUnContact() {
  if (estAppliNative()) {
    // Dans l'application : le sélecteur de contacts du téléphone, comme dans
    // les autres applications. Rien à régler, aucune fiche à exporter.
    const { Contacts } = await import('@capacitor-community/contacts');
    const choix = await Contacts.pickContact({
      projection: {
        name: true,
        organization: true,
        phones: true,
        emails: true,
        postalAddresses: true,
      },
    });
    return choix && choix.contact ? contactDuNatif(choix.contact) : null;
  }
  // Aucune attente avant select() : Safari n'ouvre le carnet que dans le
  // geste du doigt. Les champs ont été appris à l'affichage du bouton.
  const choix = await navigator.contacts.select(champsDuCarnet || CHAMPS_SURS, {
    multiple: false,
  });
  return choix && choix[0] ? contactDuCarnet(choix[0]) : null;
}

/**
 * Lit une fiche contact choisie en fichier (`.vcf`).
 *
 * <p>C'est le chemin de l'iPhone : Safari n'ouvre pas le carnet d'adresses,
 * mais l'application Contacts partage une fiche en `.vcf`, que le sélecteur de
 * fichiers propose.
 *
 * @param {Blob} fichier fiche choisie
 * @returns {Promise<{nom: string, adr: string, npa: string, tel: string, email: string}>}
 *   les champs du contact
 * @throws {Error} si le fichier ne peut pas être lu
 */
export function lisFicheVCard(fichier) {
  return new Promise((resout, rejette) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resout(contactDepuisVCard(lecteur.result));
    lecteur.onerror = () => rejette(new Error('La fiche contact n’a pas pu être lue.'));
    lecteur.readAsText(fichier);
  });
}
